import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-label", "centered", "children", "contentAreaFlush", "fullWidth", "headerComponent", "id", "isEditor", "narrow", "role", "tabIndex"];
import PropTypes from 'prop-types';
import styled from 'foundations-theming/utils';
import { BREAKPOINTS } from '../Grid/Grid.types';
import { filterProps } from '../../../utils/filterProps';
import { Outer } from '../Header/Header';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import I18n from 'I18n';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import { StyledToolBar } from '../ToolBar/ToolBar';
import { StyledFlex } from '../../../components/Flex/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
const LAYOUT_MAX_WIDTH = '1280px';
const LAYOUT_NARROW_WIDTH = '1140px';
const StyledSection = styled.section.withConfig({
  displayName: "AbstractPageSection__StyledSection",
  componentId: "sc-1e0l9ht-0"
})(({
  theme,
  $centered,
  $contentAreaFlush,
  $fullWidth,
  $headerComponent,
  $isEditor,
  $narrow,
  $cssStyles
}) => {
  const mediumMediaQuery = `@media (max-width: ${BREAKPOINTS.md}px)`;
  const smallMediaQuery = `@media (max-width: ${BREAKPOINTS.sm}px)`;
  const maxWidth = !$fullWidth ? $narrow ? LAYOUT_NARROW_WIDTH : `calc(${LAYOUT_MAX_WIDTH} + (${theme.space['1100']} * 2))` // space token usage is valid since the purpose is to compensate for the padding
  : undefined;
  const shouldApplyPadding = $headerComponent != null || !$contentAreaFlush;
  return Object.assign({
    marginInline: $centered ? 'auto' : undefined,
    width: $centered ? '100%' : undefined,
    maxWidth,
    position: $isEditor ? 'relative' : undefined,
    height: $isEditor ? '100%' : undefined,
    paddingInline: !shouldApplyPadding ? theme.space['0'] : !$isEditor ? theme.space['1100'] : undefined,
    [mediumMediaQuery]: {
      paddingInline: !shouldApplyPadding ? theme.space['0'] : !$isEditor ? theme.space['600'] : undefined
    },
    [smallMediaQuery]: {
      paddingInline: !shouldApplyPadding ? theme.space['0'] : !$isEditor ? theme.space['400'] : undefined
    }
  }, $headerComponent && {
    [`& ${Outer}`]: {
      borderBlockEnd: 0,
      marginBlockEnd: theme.space['0'],
      paddingInline: !$isEditor ? `${theme.space['0']} !important` : undefined
    }
  }, {
    [`& ${StyledToolBar} > ${StyledFlex}`]: {
      borderBlockEnd: theme.borderWidth['0']
    },
    flexGrow: 1,
    [CSS_SPECIFICITY.OVERRIDE]: Object.assign({}, $cssStyles)
  });
});
export const AbstractPageSection = _ref => {
  let {
      'aria-label': ariaLabel = I18n.text('ui.UIAbstractPageSection.page'),
      centered = false,
      children,
      contentAreaFlush = false,
      fullWidth = false,
      headerComponent,
      id,
      isEditor = false,
      narrow = false,
      role,
      tabIndex
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(StyledSection, Object.assign({
    "aria-label": ariaLabel,
    $centered: centered,
    $contentAreaFlush: contentAreaFlush,
    "data-full-width": fullWidth ? 'true' : 'false',
    $fullWidth: fullWidth,
    $headerComponent: headerComponent,
    id: id,
    $isEditor: isEditor,
    $narrow: narrow,
    role: role,
    tabIndex: tabIndex
  }, domProps, {
    $cssStyles: cssStyles,
    children: children
  }));
};
AbstractPageSection.propTypes = {
  children: PropTypes.node
};
AbstractPageSection.displayName = 'AbstractPageSection';